import React, { useState } from 'react';
import { DefaultThumbnail } from '@assets';
import AppConfig from '@config';
import { IFile } from '@routes/Common/type';
import useFiles from '@hooks/useFiles';
import Loading from '@components/Loading';
import { colorSet, useSnackbar } from '@components/Provider';
import { CustomizedTypography, CustomizedButton } from '@components/Customized';
import { RfqDetailData } from '@routes/Common/type';
import { Box, makeStyles, Theme } from '@material-ui/core';
import { Lock } from '@material-ui/icons';
import { useStylesCommon } from './useStyles';
import { CapaPcbServiceType } from '@generated/graphql';
import { Download } from '@carbon/icons-react';

interface UseStylesProps {
  matches?: boolean;
  assemblyOnly: boolean;
}

const useStyles = makeStyles<Theme, UseStylesProps>(() => {
  return {
    boxTitle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 16,
      '& svg': {
        fontSize: '20px !important'
      }
    },
    displayItemHeader: {
      display: 'flex',
      alignItems: 'center',
      height: 36,
      borderBottom: `1px solid ${colorSet.gray200}`,
      '& :nth-child(1)': {
        width: 'calc(100% - 180px)'
      },
      '& :nth-child(2)': {
        width: 120
      },
      '& :nth-child(3)': {
        width: 90
      },
      justifyContent: ({ matches }) => (matches ? 'space-between' : '')
    },
    displayItem: {
      display: 'flex',
      alignItems: 'center',
      minHeight: 100,
      justifyContent: ({ assemblyOnly }) =>
        assemblyOnly ? 'flex-start' : 'space-between',
      padding: '12px 0px',
      '& .MuiTypography-colorTextSecondary': {
        color: colorSet.gray600
      },
      borderBottom: `1px solid ${colorSet.gray200}`
    },
    itemIndex: {
      width: 24
    },
    thumbnail: {
      width: 66,
      height: 66,
      border: `1px solid ${colorSet.gray200}`,
      borderRadius: 8
    },
    fileInfo: {
      display: 'flex',
      flexDirection: (props) => (props.matches ? 'column' : 'row'),
      alignItems: (props) => (props.matches ? 'flex-start' : 'center'),
      width: (props) =>
        props.matches
          ? 'calc(100% - 23px - 66px)'
          : 'calc(100% - 32px - 66px - 200px)',
      paddingLeft: (props) => (props.matches ? 12 : 16),
      '& .MuiSvgIcon-root': {
        cursor: 'pointer',
        color: colorSet.gray700,
        marginLeft: 4
      }
    },
    fileNameWrapper: {
      display: 'flex',
      alignItems: 'center',
      width: (props) => (props.matches ? '100%' : 'calc(100% - 30px)')
    },
    fileName: {
      display: 'flex',
      alignItems: 'center',
      width: (props) => (props.matches ? 185 : 230),
      '& > p': {
        marginRight: 4
      },
      '& > svg': {
        minWidth: 20,
        '&:hover': {
          cursor: 'pointer'
        }
      }
    },
    material: {
      width: (props) => (props.matches ? '100%' : 120),
      padding: (props) => (props.matches ? 0 : '0px 8px')
    },
    quantity: {
      width: (props) => (props.matches ? '100%' : 90)
    },
    totalQuantity: {
      '& span': {
        color: colorSet.primary500
      },
      marginTop: (props) => (props.matches ? 12 : 16)
    },
    publicClose: {
      width: '100%',
      height: 44,
      borderRadius: 8,
      marginBottom: 16,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: colorSet.errorBackGround,
      '& > svg': {
        marginRight: 8
      },
      '& > p': {
        color: colorSet.errorMain
      }
    }
  };
});

interface Props {
  data?: RfqDetailData;
  matches?: boolean;
}

const RfqPcbProduceFilesPresenter: React.FC<Props> = ({ data, matches }) => {
  const pcbServices =
    data?.pcb?.services?.map((service) => service.serviceType) ?? [];
  const assemblyOnly =
    pcbServices.includes(CapaPcbServiceType.CircuitAssembly) &&
    !pcbServices.includes(CapaPcbServiceType.CircuitFabrication);
  const classes = useStyles({ matches: matches, assemblyOnly });
  const classesCommon = useStylesCommon({ matches: matches });
  const { enqueueSnackbar } = useSnackbar();
  const { onDownloadFile, onDownloadZipFile } = useFiles();
  const [isLoading, setIsLoading] = useState(false);
  const publicOpen = data?.publicOpen;
  const canDownload = true;
  const fabricationFiles = data?.pcb?.fabricationFiles ?? [];

  const onCheckFilePermission = () => {
    if (!publicOpen && !canDownload) {
      enqueueSnackbar('도면 다운로드 권한이 제한된 프로젝트입니다.', {
        variant: 'warning'
      });
      return false;
    }
    return true;
  };

  const onFileDownLoad = (file: IFile) => {
    if (!onCheckFilePermission()) {
      return;
    } else {
      onDownloadFile(file)
        .then((resp) => {
          if (!resp) {
            enqueueSnackbar(`파일 다운로드에 실패했습니다.`, {
              variant: 'error'
            });
          }
        })
        .catch(() => {
          enqueueSnackbar(`파일 다운로드에 실패했습니다.`, {
            variant: 'error'
          });
        });
    }
  };

  const onFileDownloadAll = () => {
    if (fabricationFiles.length < 1) return false;

    const fileArr: Array<IFile> = [];
    setIsLoading(true);

    if (!onCheckFilePermission()) {
      setIsLoading(false);
      return;
    } else {
      fabricationFiles.map((file) => {
        if (file.fileId && file.fileName) {
          fileArr.push({ fileId: file.fileId, fileName: file.fileName });
        }
        return null;
      });

      if (fileArr.length > 1) {
        onDownloadZipFile(fileArr)
          .then(() => {
            setIsLoading(false);
          })
          .catch(() => {
            setIsLoading(false);
            enqueueSnackbar(`파일 다운로드에 실패했습니다.`, {
              variant: 'error'
            });
          });
      } else if (fileArr.length === 1) {
        setIsLoading(false);
        onFileDownLoad(fileArr[0]);
      }
    }
  };

  const ImageThumbnail = ({ fileId }: { fileId: string }) => {
    const imageUrl = `${AppConfig.hoopsViewerAPI}/${fileId}.png`;
    const thumbnailImg = fileId ? imageUrl : DefaultThumbnail;
    const [error, setError] = useState(false);

    return (
      <img
        className={classes.thumbnail}
        src={error ? DefaultThumbnail : thumbnailImg}
        alt="iamge"
        onError={() => setError(true)}
      />
    );
  };

  return (
    <React.Fragment>
      <Loading open={isLoading} />
      <Box className={classesCommon.root + ' item'}>
        <div className={classes.boxTitle}>
          <CustomizedTypography
            variant={'h4'}
            color="textPrimary"
            fontWeight="500"
          >
            제작파일
          </CustomizedTypography>
          {(publicOpen || canDownload) && fabricationFiles.length > 0 && (
            <CustomizedButton
              variant="text"
              size="large"
              startIcon={<Download />}
              onClick={onFileDownloadAll}
            >
              전체 다운로드
            </CustomizedButton>
          )}
        </div>
        {!publicOpen && (
          <div className={classes.publicClose}>
            <Lock color="error" />
            <CustomizedTypography variant="body2" fontWeight="500">
              제작파일 다운로드를 제한하였습니다
            </CustomizedTypography>
          </div>
        )}
        {fabricationFiles.length > 0 ? (
          <>
            <div className={classes.displayItemHeader}>
              <CustomizedTypography variant="subtitle2" color="textSecondary">
                항목
              </CustomizedTypography>
              {!assemblyOnly && (
                <>
                  {!matches && (
                    <CustomizedTypography
                      variant="subtitle2"
                      color="textSecondary"
                    >
                      두께
                    </CustomizedTypography>
                  )}

                  <CustomizedTypography
                    variant="subtitle2"
                    color="textSecondary"
                    align="right"
                    noWrap
                  >
                    제작수량(PCS)
                  </CustomizedTypography>
                </>
              )}
            </div>
            {fabricationFiles.map((file, index) => {
              const {
                fileId,
                fileName,
                quantity: fileQuantity,
                thickness: fileThickness
              } = file;
              const quantity = fileQuantity ?? 0;
              const thickness = fileThickness ?? 0;

              return (
                <div key={file.fileId} className={classes.displayItem}>
                  <CustomizedTypography
                    className={classes.itemIndex}
                    variant="body2"
                    color="textSecondary"
                  >
                    {index + 1}
                  </CustomizedTypography>
                  <ImageThumbnail fileId={fileId} />
                  <div className={classes.fileInfo}>
                    <div className={classes.fileNameWrapper}>
                      <div className={classes.fileName}>
                        <CustomizedTypography
                          variant="body1"
                          color="textPrimary"
                          gutterBottoms={matches ? 4 : 0}
                          noWrap
                        >
                          {fileName}
                        </CustomizedTypography>
                        {(publicOpen || canDownload) && (
                          <Download
                            width={20}
                            height={20}
                            color={colorSet.primary500}
                            onClick={() => {
                              onFileDownLoad(file);
                            }}
                          />
                        )}
                      </div>
                      {matches && !assemblyOnly && (
                        <CustomizedTypography
                          className={classes.quantity}
                          variant="body1"
                          align="right"
                          fontWeight="500"
                        >
                          {quantity.toLocaleString('kr')}개
                        </CustomizedTypography>
                      )}
                    </div>
                    {matches && !assemblyOnly && (
                      <CustomizedTypography
                        className={classes.quantity}
                        color={'textSecondary'}
                        variant="body1"
                        style={{ wordBreak: 'break-all' }}
                      >
                        두께: {thickness}
                        mm
                      </CustomizedTypography>
                    )}
                  </div>

                  {!matches && !assemblyOnly && (
                    <CustomizedTypography
                      className={classes.material}
                      variant="body1"
                    >
                      {thickness} mm
                    </CustomizedTypography>
                  )}
                  {!matches && !assemblyOnly && (
                    <CustomizedTypography
                      className={classes.quantity}
                      variant="body1"
                      fontWeight="500"
                      align="right"
                    >
                      {quantity.toLocaleString('kr')}개
                    </CustomizedTypography>
                  )}
                </div>
              );
            })}
          </>
        ) : (
          <CustomizedTypography
            variant={matches ? 'body2' : 'body1'}
            color="textSecondary"
          >
            제작 파일이 없습니다.
          </CustomizedTypography>
        )}
      </Box>
    </React.Fragment>
  );
};

export default RfqPcbProduceFilesPresenter;
